import React, { useState, useEffect, useRef } from 'react';
import { Toast } from 'primereact/toast';
import { Button } from 'primereact/button';
import { attendanceService } from '../../services/attendanceService';
import { classesAPI } from '../../services/classService';
import { useAuth } from '../../contexts/AuthContext';
import AttendanceFilters from './AttendanceFilters';
import HolidayDialog from './HolidayDialog';

const Attendance = () => {
  const { user } = useAuth();
  const toast = useRef(null);
  const teacherId = localStorage.getItem('employeeId');

  const [loading, setLoading] = useState(false);
  const [classes, setClasses] = useState([]);
  const [sections, setSections] = useState([]);
  const [students, setStudents] = useState([]);
  const [isHoliday, setIsHoliday] = useState(false);

  const [selectedClass, setSelectedClass] = useState(null);
  const [selectedSection, setSelectedSection] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [attendanceSubmitted, setAttendanceSubmitted] = useState(false);
  const [holidays, setHolidays] = useState([]);
  const [viewMode, setViewMode] = useState('attendance'); // 'attendance' or 'holidays'
  const [holidayFilterType, setHolidayFilterType] = useState('month'); // 'month' or 'year'
  const [holidaySearchQuery, setHolidaySearchQuery] = useState('');

  const [holidayDialog, setHolidayDialog] = useState(false);
  const [holidayForm, setHolidayForm] = useState({
    date: new Date(),
    title: '',
    description: ''
  });

  const formatDate = (date) => {
    if (!date) return '';
    const d = new Date(date);
    let month = '' + (d.getMonth() + 1);
    let day = '' + d.getDate();
    const year = d.getFullYear();

    if (month.length < 2) month = '0' + month;
    if (day.length < 2) day = '0' + day;

    return [year, month, day].join('-');
  };

  const attendanceOptions = [
    { label: 'Present', value: 'present', icon: 'pi pi-check-circle', color: 'text-green-600', bg: 'bg-green-50', border: 'border-green-200' },
    { label: 'Absent', value: 'absent', icon: 'pi pi-times-circle', color: 'text-red-600', bg: 'bg-red-50', border: 'border-red-200' },
    // { label: 'Late', value: 'late', icon: 'pi pi-clock', color: 'text-orange-600', bg: 'bg-orange-50', border: 'border-orange-200' }
  ];

  useEffect(() => {
    loadClasses();
  }, []);

  useEffect(() => {
    if (selectedClass) {
      loadSections(selectedClass);
    } else {
      setSections([]);
      setSelectedSection(null);
    }
  }, [selectedClass]);

  useEffect(() => {
    if (selectedClass && selectedSection && selectedDate && viewMode === 'attendance') {
      loadStudents();
    }
  }, [selectedClass, selectedSection, selectedDate, viewMode]);

  useEffect(() => {
    if (viewMode === 'holidays') {
      loadHolidays();
    }
  }, [viewMode, holidayFilterType, selectedDate]);

  const loadClasses = async () => {
    try {
      const response = await classesAPI.getAllclasses();
      setClasses(response.data.data || []);
    } catch (err) {
      console.error('Failed to load classes', err);
    }
  };

  const loadSections = async (classId) => {
    try {
      const response = await classesAPI.getsectionsbyclass(classId);
      setSections(response.data.data || []);
    } catch (err) {
      console.error('Failed to load sections', err);
    }
  };

  const loadStudents = async () => {
    setLoading(true);
    try {
      const dateStr = formatDate(selectedDate);
      const response = await attendanceService.getStudentAttendance(selectedClass, selectedSection, dateStr);

      const storageKey = `attendance_${selectedClass}_${selectedSection}_${dateStr}`;
      const isStoredSubmitted = localStorage.getItem(storageKey) === 'true';

      if (response.data.holiday) {
        setIsHoliday(true);
        setStudents([]);
        setAttendanceSubmitted(false);
      } else {
        setIsHoliday(false);
        // Initialize attendance status to null if not already set
        const studentsData = (response.data.students || []).map(s => ({
          ...s,
          status: s.status || null
        }));
        setStudents(studentsData);

        // Check if attendance is already marked either in localStorage or if all students have status from API
        const allMarked = studentsData.length > 0 && studentsData.every(s => s.status !== null);
        setAttendanceSubmitted(isStoredSubmitted || allMarked);
      }
    } catch (err) {
      console.error('Failed to load students', err);
      toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to load students' });
    } finally {
      setLoading(false);
    }
  };

  const loadHolidays = async () => {
    try {
      setLoading(true);
      const month = holidayFilterType === 'month' ? selectedDate.getMonth() + 1 : null;
      const year = selectedDate.getFullYear();
      const response = await attendanceService.getHolidays(month, year);
      setHolidays(response.data.holidays || []);
      // setShowHolidaysDialog(true);
    } catch (err) {
      console.error('Failed to load holidays', err);
      toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to load holidays' });
    } finally {
      setLoading(false);
    }
  };

  const onAttendanceChange = (studentId, status) => {
    setStudents(prev => prev.map(s => s.id === studentId ? { ...s, status } : s));
  };

  const handleSaveAttendance = async () => {
    if (!students.length) return;

    setLoading(true);
    try {
      const dateStr = formatDate(selectedDate);
      const attendanceData = {
        date: dateStr,
        classId: selectedClass,
        sectionId: selectedSection,
        teacherId: parseInt(teacherId),
        attendance: students.map(s => ({
          studentId: s.id,
          status: s.status
        }))
      };

      await attendanceService.markAttendance(attendanceData);

      // Save persistence in localStorage
      const storageKey = `attendance_${selectedClass}_${selectedSection}_${dateStr}`;
      localStorage.setItem(storageKey, 'true');
      setAttendanceSubmitted(true);

      toast.current?.show({ severity: 'success', summary: 'Success', detail: 'Attendance marked successfully' });
    } catch (err) {
      console.error('Failed to mark attendance', err);
      toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to mark attendance or Attendance Already Marked' });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveHoliday = async () => {
    if (!holidayForm.date || !holidayForm.title) {
      toast.current?.show({
        severity: 'warn',
        summary: 'Warning',
        detail: 'Please fill required fields'
      });
      return;
    }

    setLoading(true);
    try {
      const dateStr = formatDate(holidayForm.date);
      const holidayData = {
        ...holidayForm,
        date: dateStr,
        teacherId: parseInt(teacherId)
      };

      const res = await attendanceService.setHoliday(holidayData);

      if (res.data.success === false) {
        toast.current?.show({
          severity: 'info',
          summary: 'Info',
          detail: res.data.message   // "Holiday already set for this date"
        });
        setHolidayDialog(false);
        return;
      }

      toast.current?.show({
        severity: 'success',
        summary: 'Success',
        detail: 'Holiday set successfully'
      });

      setHolidayDialog(false);
      setHolidayForm({ date: new Date(), title: '', description: '' });

      if (dateStr === formatDate(selectedDate)) {
        loadStudents();
      }

      if (viewMode === 'holidays') {
        loadHolidays();
      }

    } catch (err) {
      console.error('Failed to set holiday', err);
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to set holiday'
      });
    } finally {
      setLoading(false);
    }
  };

  const renderAttendanceItem = (student) => {
    return (
      <div key={student.id} className="flex items-center justify-between p-3 bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-all">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-bold">
            {student.firstName?.[0]}{student.lastName?.[0]}
          </div>
          <div>
            <h4 className="text-sm font-bold text-gray-900">{student.firstName} {student.lastName}</h4>
            <p className="text-xs text-gray-500">ID: {student.id}</p>
          </div>
        </div>

        <div className="flex gap-2">
          {attendanceOptions.map(option => (
            <button
              key={option.value}
              onClick={() => onAttendanceChange(student.id, option.value)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-semibold transition-all ${student.status === option.value
                  ? `${option.bg} ${option.border} ${option.color} shadow-sm`
                  : 'bg-white border-gray-200 text-gray-400 hover:bg-gray-50'
                }`}
            >
              <i className={`${option.icon} ${student.status === option.value ? option.color : 'text-gray-400'}`}></i>
              {option.label}
            </button>
          ))}
        </div>
      </div>
    );
  };

  const renderHolidayItem = (holiday) => {
    const date = new Date(holiday.date);
    return (
      <div key={holiday.id || holiday.date} className="flex items-center justify-between p-4 bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-all border-l-4 border-l-orange-500">
        <div className="flex items-center gap-4">
          <div className="flex flex-col items-center justify-center w-14 h-14 rounded-lg bg-orange-50 text-orange-600 border border-orange-100">
            <span className="text-xs font-bold uppercase">{date.toLocaleString('default', { month: 'short' })}</span>
            <span className="text-xl font-black">{date.getDate()}</span>
          </div>
          <div>
            <h4 className="text-base font-bold text-gray-900">{holiday.title}</h4>
            <p className="text-sm text-gray-500 line-clamp-1">{holiday.description || 'No description'}</p>
            <p className="text-xs text-gray-400 mt-0.5">
              <i className="pi pi-calendar mr-1"></i>
              {date.toLocaleDateString(undefined, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
            </p>
          </div>
        </div>
        <div className="p-2 bg-orange-50 rounded-full">
          <i className="pi pi-sun text-orange-500"></i>
        </div>
      </div>
    );
  };

  const filteredHolidays = holidays.filter(h => 
    h.title?.toLowerCase().includes(holidaySearchQuery.toLowerCase()) ||
    h.description?.toLowerCase().includes(holidaySearchQuery.toLowerCase())
  );

  return (
    <div style={{ height: 'calc(100vh - 64px)' }} className="flex flex-col overflow-hidden p-4">
      <Toast ref={toast} />

      <AttendanceFilters
        selectedClass={selectedClass}
        classes={classes}
        onClassFilterChange={(e) => setSelectedClass(e.value)}
        selectedSection={selectedSection}
        sections={sections}
        onSectionFilterChange={(e) => setSelectedSection(e.value)}
        selectedDate={selectedDate}
        onDateChange={setSelectedDate}
        handleSetHoliday={() => setHolidayDialog(true)}
        handleShowHolidays={() => setViewMode(viewMode === 'attendance' ? 'holidays' : 'attendance')}
        user={user}
        viewMode={viewMode}
        holidayFilterType={holidayFilterType}
        setHolidayFilterType={setHolidayFilterType}
        holidaySearchQuery={holidaySearchQuery}
        setHolidaySearchQuery={setHolidaySearchQuery}
      />

      <div className="bg-white/80 backdrop-blur-sm rounded-xl border border-white/20 shadow-xl overflow-hidden flex flex-col flex-1 min-h-0 mt-2">
        <div className="border-b border-gray-100 p-4 flex-shrink-0 flex justify-between items-center">
          <div>
            <h3 className="text-xl font-bold text-gray-900">
              {viewMode === 'attendance' ? 'Attendance Directory' : 'Holidays List'}
            </h3>
            <p className="text-xs text-gray-500">
              {viewMode === 'attendance' ? 'Mark attendance for students' : 'School scheduled holidays'}
            </p>
          </div>

          {viewMode === 'attendance' && !isHoliday && students.length > 0 && !attendanceSubmitted && students.every(s => s.status === 'present' || s.status === 'absent') && (
            <Button
              label="Submit Attendance"
              icon="pi pi-check"
              onClick={handleSaveAttendance}
              loading={loading}
              className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 py-2 rounded-lg shadow-lg hover:shadow-xl transition-all font-bold text-sm"
            />
          )}
        </div>

        <div className="p-4 overflow-y-auto flex-1 min-h-0">
          {loading && (viewMode === 'attendance' ? students.length === 0 : holidays.length === 0) ? (
            <div className="flex justify-center items-center h-full">
              <div className="flex flex-col items-center space-y-4">
                <div className="w-12 h-12 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin"></div>
                <span className="text-sm font-medium text-gray-600">Loading {viewMode}...</span>
              </div>
            </div>
          ) : viewMode === 'holidays' ? (
            filteredHolidays.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full space-y-4">
                <i className="pi pi-calendar-times text-6xl text-gray-200"></i>
                <p className="text-gray-500 font-medium">No holidays found for this selection</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-4 pb-4">
                {filteredHolidays.map(renderHolidayItem)}
              </div>
            )
          ) : isHoliday ? (
            <div className="flex flex-col items-center justify-center h-full space-y-4">
              <div className="p-6 bg-orange-50 rounded-full">
                <i className="pi pi-sun text-6xl text-orange-500"></i>
              </div>
              <div className="text-center">
                <h3 className="text-2xl font-bold text-gray-900">It's a Holiday!</h3>
                <p className="text-gray-600 mt-2">Attendance marking is disabled for this date.</p>
              </div>
            </div>
          ) : attendanceSubmitted ? (
            <div className="flex flex-col items-center justify-center h-full space-y-4">
              <div className="p-6 bg-green-50 rounded-full">
                <i className="pi pi-check-circle text-6xl text-green-500"></i>
              </div>
              <div className="text-center">
                <h3 className="text-2xl font-bold text-gray-900">Today attendance marked</h3>
                <p className="text-gray-600 mt-2">The attendance for this class and section has been successfully recorded.</p>
              </div>
            </div>
          ) : !selectedClass || !selectedSection ? (
            <div className="flex flex-col items-center justify-center h-full space-y-4 opacity-60">
              <i className="pi pi-search text-6xl text-gray-300"></i>
              <div className="text-center">
                <h3 className="text-xl font-bold text-gray-400">Select Filters</h3>
                <p className="text-gray-400 mt-1 text-sm">Choose class and section to see students</p>
              </div>
            </div>
          ) : students.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full space-y-4">
              <i className="pi pi-users text-6xl text-gray-200"></i>
              <p className="text-gray-500 font-medium">No students found for this selection</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-4 pb-4">
              {students.map(renderAttendanceItem)}
            </div>
          )}
        </div>
      </div>

      <HolidayDialog
        visible={holidayDialog}
        onHide={() => setHolidayDialog(false)}
        holidayForm={holidayForm}
        setHolidayForm={setHolidayForm}
        handleSaveHoliday={handleSaveHoliday}
        loading={loading}
      />
    </div>
  );
};

export default Attendance;
